import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, MONTHS_ES, formatMoney } from "../lib/api";
import { Plus, CalendarBlank, TrendUp, TrendDown, Wallet, ArrowRight, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Dashboard() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    initial_balance: 0,
    description: "",
    template_id: "none",
  });
  const [templates, setTemplates] = useState([]);

  const handleTemplateChange = (id) => {
    const tpl = templates.find((t) => t.id === id);
    setForm((f) => ({
      ...f,
      template_id: id,
      initial_balance: tpl ? tpl.initial_balance : f.initial_balance,
      name: tpl && !f.name.trim() ? tpl.name : f.name,
    }));
  };

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await api.listPlans();
      setPlans(data);
    } catch (e) {
      toast.error("Error al cargar planes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    if (open) {
      api.listTemplates().then(setTemplates).catch(() => setTemplates([]));
    }
  }, [open]);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error("Ingresa un nombre para el plan");
      return;
    }
    try {
      await api.createPlan({
        ...form,
        year: parseInt(form.year),
        month: parseInt(form.month),
        initial_balance: parseFloat(form.initial_balance) || 0,
        template_id: form.template_id === "none" ? null : form.template_id,
      });
      toast.success("Plan creado");
      setOpen(false);
      setForm({ ...form, name: "", description: "", initial_balance: 0, template_id: "none" });
      loadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear plan");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}" y todas sus versiones?`)) return;
    try {
      await api.deletePlan(id);
      toast.success("Plan eliminado");
      loadPlans();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, i) => currentYear - 2 + i);

  return (
    <div>
      {/* Top bar */}
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div>
          <div className="label-xs text-muted-foreground">Workspace</div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight">Planificación Financiera</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-plan-btn" className="rounded-sm font-semibold">
              <Plus size={16} weight="bold" className="mr-2" />
              Nuevo Plan
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-plan-dialog" className="rounded-sm max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">
                Nuevo Plan Financiero
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div>
                <Label className="label-xs text-muted-foreground">Plantilla</Label>
                <Select value={form.template_id} onValueChange={handleTemplateChange}>
                  <SelectTrigger data-testid="plan-template-select" className="mt-1 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Plan en blanco</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name} · {t.entries.length} partidas</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.template_id !== "none" && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="plan-template-hint">
                    Las partidas de la plantilla se copiarán a la V1 con fechas del mes elegido.
                  </p>
                )}
              </div>
              <div>
                <Label className="label-xs text-muted-foreground">Nombre del plan</Label>
                <Input
                  data-testid="plan-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Forecast Operativo"
                  className="mt-1 rounded-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-xs text-muted-foreground">Mes</Label>
                  <Select
                    value={String(form.month)}
                    onValueChange={(v) => setForm({ ...form, month: v })}
                  >
                    <SelectTrigger data-testid="plan-month-select" className="mt-1 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS_ES.slice(1).map((m, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-xs text-muted-foreground">Año</Label>
                  <Select
                    value={String(form.year)}
                    onValueChange={(v) => setForm({ ...form, year: v })}
                  >
                    <SelectTrigger data-testid="plan-year-select" className="mt-1 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="label-xs text-muted-foreground">Saldo inicial de caja (USD)</Label>
                <Input
                  data-testid="plan-initial-balance-input"
                  type="number"
                  step="0.01"
                  value={form.initial_balance}
                  onChange={(e) => setForm({ ...form, initial_balance: e.target.value })}
                  className="mt-1 rounded-sm font-mono-tabular"
                />
              </div>
              <div>
                <Label className="label-xs text-muted-foreground">Descripción (opcional)</Label>
                <Textarea
                  data-testid="plan-description-input"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 rounded-sm"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="confirm-new-plan-btn"
                onClick={handleCreate}
                className="rounded-sm font-semibold"
              >
                Crear Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-6">
        {/* Stat strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatBox label="Planes Totales" value={plans.length} icon={CalendarBlank} />
          <StatBox label="Año actual" value={currentYear} icon={TrendUp} accent="emerald" />
          <StatBox label="Moneda" value="USD" icon={Wallet} accent="slate" />
        </div>

        {/* Plans grid */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold tracking-tight">Planes Financieros</h2>
          <span className="label-xs text-muted-foreground">{plans.length} en total</span>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Cargando…</div>
        ) : plans.length === 0 ? (
          <div className="border border-dashed border-border rounded-sm p-14 text-center">
            <CalendarBlank size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
            <h3 className="font-heading font-bold text-lg">No hay planes todavía</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Crea tu primer plan mensual para comenzar a proyectar tu flujo de caja.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
            {plans.map((p) => (
              <PlanCard key={p.id} plan={p} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, accent }) {
  return (
    <div className="card-elevated p-5 flex items-start justify-between">
      <div>
        <div className="label-xs text-muted-foreground">{label}</div>
        <div className="font-heading text-3xl font-black tracking-tight mt-1 font-mono-tabular">
          {value}
        </div>
      </div>
      <div className={`p-2 rounded-sm ${accent === "emerald" ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950 dark:text-emerald-400" : "text-slate-700 bg-slate-100 dark:bg-slate-800 dark:text-slate-300"}`}>
        <Icon size={20} weight="duotone" />
      </div>
    </div>
  );
}

function PlanCard({ plan, onDelete }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.getPlan(plan.id).then((d) => setSummary(d)).catch(() => {});
  }, [plan.id]);

  const totals = summary?.totals || {};
  const final = totals.final_balance;
  const isPos = (final ?? 0) >= 0;

  return (
    <div className="card-elevated hover:border-foreground/30 transition-colors group" data-testid={`plan-card-${plan.id}`}>
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-xs text-muted-foreground">
              {MONTHS_ES[plan.month]} · {plan.year}
            </div>
            <h3 className="font-heading font-extrabold text-lg mt-1 tracking-tight">{plan.name}</h3>
          </div>
          <button
            data-testid={`delete-plan-${plan.id}`}
            onClick={() => onDelete(plan.id, plan.name)}
            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-sm text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
          >
            <Trash size={16} />
          </button>
        </div>

        {summary && (
          <div className="mt-4 space-y-2 text-sm">
            <Row label="Saldo inicial" value={formatMoney(summary.current_version?.initial_balance || 0)} />
            <Row label="Ingresos" value={formatMoney(totals.total_income || 0)} positive />
            <Row label="Egresos" value={formatMoney(totals.total_expense || 0)} negative />
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-border flex items-end justify-between">
          <div>
            <div className="label-xs text-muted-foreground">Saldo final proyectado</div>
            <div className={`font-heading font-black text-2xl mt-1 font-mono-tabular ${isPos ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
              {formatMoney(final || 0)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {plan.template_name && (
              <span className="label-xs text-muted-foreground" data-testid={`plan-template-badge-${plan.id}`}>{plan.template_name}</span>
            )}
            <span className="label-xs px-2 py-1 bg-muted rounded-sm">V{plan.current_version_number}</span>
          </div>
        </div>
      </div>
      <Link
        to={`/plans/${plan.id}`}
        data-testid={`open-plan-${plan.id}`}
        className="flex items-center justify-between px-5 py-3 border-t border-border text-sm font-semibold hover:bg-muted transition-colors"
      >
        Abrir plan <ArrowRight size={16} weight="bold" />
      </Link>
    </div>
  );
}

function Row({ label, value, positive, negative }) {
  const cls = positive ? "text-emerald-700 dark:text-emerald-400" : negative ? "text-red-700 dark:text-red-400" : "";
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono-tabular font-semibold ${cls}`}>{value}</span>
    </div>
  );
}
